import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, X, ImageIcon, FileSignature } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

/**
 * ReportBrandingCard — allows a signed-in client to configure the cover page
 * of their monthly Growth Report PDF (client name, logo, prepared-by signer).
 */
export default function ReportBrandingCard() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/my/report-branding`, { headers: httpAuth() })
      .then((r) => setB(r.data))
      .catch(() => setB({}));
  }, []);

  if (!b) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/my/report-branding`, patch,
        { headers: httpAuth() });
      setB(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) {
      toast.error("Logo must be under 2.5 MB");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      const raw = String(dataUrl).split(",")[1] || "";
      await save({ logo_base64: raw, logo_content_type: file.type });
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearLogo = async () => {
    if (!window.confirm("Remove your uploaded logo?")) return;
    await save({ clear_logo: true });
  };

  const logoSrc = b.has_logo
    ? `${API}/my/report-branding/logo?ts=${b.updated_at || ""}`
    : "";

  return (
    <Card className="p-6 border border-slate-200 rounded-2xl mb-8"
          data-testid="report-branding-card">
      <div className="flex items-start gap-3 mb-4">
        <FileSignature className="w-5 h-5 text-indigo-600 mt-0.5" />
        <div>
          <div className="font-bold text-slate-900">Growth Report cover page</div>
          <p className="text-xs text-slate-500 mt-0.5">
            Make the monthly PDF board-ready. Your logo, your business name,
            and a signature line make it feel like a real quarterly review.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Logo */}
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Client logo</div>
          <div className="w-full aspect-square max-h-40 rounded-xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center overflow-hidden">
            {b.has_logo ? (
              <img src={logoSrc} alt="Client logo"
                    className="max-h-full max-w-full object-contain"
                    data-testid="branding-logo-preview" />
            ) : (
              <div className="text-slate-400 flex flex-col items-center text-xs">
                <ImageIcon className="w-6 h-6 mb-1" />
                No logo yet
              </div>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <input ref={fileRef} type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => onUpload(e.target.files?.[0])}
                    data-testid="branding-logo-input" />
            <Button size="sm" variant="outline" className="rounded-full"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="branding-logo-upload">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
              {b.has_logo ? "Replace" : "Upload"}
            </Button>
            {b.has_logo && (
              <Button size="sm" variant="outline" className="rounded-full"
                      onClick={clearLogo}
                      data-testid="branding-logo-clear">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Client / prepared-by details */}
        <div className="md:col-span-2 space-y-3">
          <label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold block">
            Client name (shown on cover)
            <input value={b.client_name || ""}
                    onChange={(e) => setB({ ...b, client_name: e.target.value })}
                    onBlur={(e) => save({ client_name: e.target.value })}
                    placeholder="Acme Dental Group"
                    className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900 normal-case tracking-normal"
                    data-testid="branding-client-name" />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold block">
              Prepared by
              <input value={b.prepared_by_name || ""}
                      onChange={(e) => setB({ ...b, prepared_by_name: e.target.value })}
                      onBlur={(e) => save({ prepared_by_name: e.target.value })}
                      placeholder="Sarah Kim"
                      className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900 normal-case tracking-normal"
                      data-testid="branding-prepared-name" />
            </label>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold block">
              Title
              <input value={b.prepared_by_title || ""}
                      onChange={(e) => setB({ ...b, prepared_by_title: e.target.value })}
                      onBlur={(e) => save({ prepared_by_title: e.target.value })}
                      placeholder="Growth Strategist · WebsiteTech"
                      className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal text-slate-900 normal-case tracking-normal"
                      data-testid="branding-prepared-title" />
            </label>
          </div>
          <p className="text-[11px] text-slate-400">
            {saving ? "Saving…" : "Changes save when you tab out of a field."}
          </p>
        </div>
      </div>
    </Card>
  );
}
